import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CoinChart,
  convertHexToRGBA,
  costFormater,
  getDuration,
  Hint,
  Preloader,
  RawButton,
  RedesignNotFoundIcon,
  Text,
} from '@common-fe/common-fe';
import styled from 'styled-components';

import { ROUTES } from '@/common';
import { TBD_TITLE, TRUE_STRING } from '@/common/constants';
import { useHistory } from '@/modules/core/hooks';
import theme from '@/styles/theme';

import { useInvestmentsHoldings, useInvestor, useIsFullLiquidation } from '../../hooks';
import useIsModelSwitching from '../../hooks/useIsModelSwitching';
import { InvestmentPaths } from '../../investments.types';
import { useGetInvestorAccountId, useInvestmentsProcessingSnapshot } from '../hook';

import { useGetTradingSummaryQuery } from './queries';

const ValueText = styled(Text)`
  font-family: 'Oswald', serif;
`;
const STORAGE_KEY = 'TRADING_ANIMATION';

const GRADIENT = `linear-gradient(180deg, ${theme?.colors?.canvas} 0%, ${
  theme?.colors?.canvas ? convertHexToRGBA(theme?.colors?.canvas, 0) : 'transparent'
});`;

const TradingSummary = () => {
  const isAnimationEnabled = useMemo(() => !localStorage.getItem(STORAGE_KEY), []);

  const { isLoading, data, counts } = useGetTradingSummaryQuery();
  const accountId = useGetInvestorAccountId();
  const {
    data: { type: investorType },
  } = useInvestor(accountId);
  const history = useHistory();
  const { details, isSuccessLoaded } = useInvestmentsHoldings(accountId);

  const { isFullLiquidation } = useIsFullLiquidation({ accountId });
  const isModelSwitching = useIsModelSwitching(accountId);
  const currentAvaiableToInvest = useMemo(() => {
    return details.availableToInvest || 0;
  }, [details.availableToInvest]);
  const { t } = useTranslation();

  useEffect(() => {
    if (isAnimationEnabled && isSuccessLoaded) {
      setTimeout(
        () => {
          localStorage.setItem(STORAGE_KEY, TRUE_STRING);
        },
        getDuration({ seconds: 2 })
      );
    }
  }, [isAnimationEnabled, isSuccessLoaded]);
  const isSnapshotShowed = useInvestmentsProcessingSnapshot();

  const handleInvestMore = useCallback(
    (initialAmount: number) => () => {
      window.scrollTo({ top: 0 });
      if (investorType === InvestmentPaths.SELF_DIRECTED) {
        history.push(ROUTES.SELF_DIRECTED_INVEST_MORE(accountId, initialAmount));
      } else if (investorType === InvestmentPaths.MANAGED) {
        history.push(`${ROUTES.BUY_SELL}?id=${accountId}&initialAmount=${initialAmount}`);
      }
    },
    [accountId, history, investorType]
  );

  const depositCoins = useMemo(() => {
    return (
      <>
        {counts.deposit === 0 ? (
          <Box height="140px" align="center" justify="center">
            <RedesignNotFoundIcon
              data-testid="trading-summary_placeholder_deposit"
              color="border1"
              size="32px"
            />
          </Box>
        ) : (
          <Box margin={{ top: '-100px' }}>
            <CoinChart
              isWithAnimation={isAnimationEnabled}
              isAccent
              count={counts.deposit}
              blockHeight={220}
              accentColors={theme.colors.coinChart}
            />
          </Box>
        )}
      </>
    );
  }, [counts.deposit, isAnimationEnabled]);

  const withdrawCoins = useMemo(() => {
    return (
      <>
        {counts.widthdraw === 0 ? (
          <Box height="140px" align="center" justify="center">
            <RedesignNotFoundIcon
              data-testid="trading-summary_placeholder_withdraw"
              color="border1"
              size="32px"
            />
          </Box>
        ) : (
          <Box margin={{ top: '-100px' }}>
            <CoinChart isWithAnimation={isAnimationEnabled} count={counts.widthdraw} blockHeight={220} />
          </Box>
        )}
      </>
    );
  }, [counts.widthdraw, isAnimationEnabled]);

  if (isLoading) {
    return (
      <Box
        fill
        align="center"
        justify="center"
        direction="column"
        height={{ min: '316px' }}
        background="canvas"
        overflow="hidden"
        round="container1Round"
      >
        <Preloader size={72} />
      </Box>
    );
  }

  return (
    <Box
      data-testid="trading-summary"
      fill
      direction="column"
      background="canvas"
      overflow="hidden"
      round="container1Round"
    >
      <Box
        fill
        justify="between"
        pad={{ left: 'l', right: 'l', bottom: 'm', top: 'm' }}
        style={{ position: 'relative' }}
      >
        <Box
          background={GRADIENT}
          style={{ top: 0, position: 'absolute', left: 0, right: 0, height: '24px', zIndex: 10 }}
        />
        <Box direction="row" justify="start" style={{ zIndex: 100 }}>
          <Hint
            hintWidth={220}
            hintElement={
              <Box width="80px">
                <p
                  style={{
                    textDecorationLine: 'underline',
                    textDecorationStyle: 'dotted',
                    textDecorationSkipInk: 'auto',
                    textDecorationColor: theme.colors.border,
                    cursor: 'pointer',
                    textUnderlineOffset: '0px',
                    textUnderlinePosition: 'from-font',
                    zIndex: 30,
                  }}
                >
                  <Text data-testid="trading-summary_title" color="textBody" weight={700}>
                    {t('Trading Summary')}
                  </Text>
                </p>
              </Box>
            }
          >
            <Text data-testid="trading-summary_hint" textAlign="center">
              {t(
                'Lifetime investment buys and sells within your portfolio. This does not include market adjustments or dividends.'
              )}
            </Text>
          </Hint>
        </Box>
        <Box direction="row" justify="around" style={{ position: 'relative', zIndex: 5 }}>
          <Box direction="column" justify="center" align="center">
            {depositCoins}
            <Text margin={{ top: 'spacing12' }} weight={400}>
              {t('Buys')}
            </Text>
            <ValueText data-testid="trading-summary_text_deposit" weight={700} size="2xl">
              {isSnapshotShowed ? TBD_TITLE : costFormater(data.deposit)}
            </ValueText>
          </Box>
          <Box direction="column" justify="center" align="center">
            {withdrawCoins}
            <Text margin={{ top: 'spacing12' }} weight={400}>
              {t('Sells')}
            </Text>
            <ValueText data-testid="trading-summary_text_withdraw" weight={700} size="2xl">
              {costFormater(data.withdraw)}
            </ValueText>
          </Box>
        </Box>
      </Box>

      <Box
        pad={{ vertical: '10px' }}
        direction="row"
        align="center"
        justify="center"
        border={{ side: 'top', color: 'border1' }}
      >
        <Box margin={{ top: 'xxxs' }}>
          <Text weight={400}>{t('Available to invest:')}</Text>
        </Box>
        <Box margin={{ left: 'xxs' }}>
          <RawButton
            disabled={isModelSwitching || !currentAvaiableToInvest || isFullLiquidation}
            onClick={handleInvestMore(currentAvaiableToInvest)}
          >
            <Text
              data-testid="trading-summary_text_available"
              weight={400}
              color={currentAvaiableToInvest ? 'textAccent' : 'textDisabled'}
            >
              {costFormater(currentAvaiableToInvest, true)}
            </Text>
          </RawButton>
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(TradingSummary);
